import { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";
import { photoSrc, mediaSrc } from "@/lib/api";

function VideoFrame({ video, index }) {
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(prefersReduced.matches);
    const observer = new IntersectionObserver(([entry]) => {
      const element = videoRef.current;
      if (!element || prefersReduced.matches) return;
      if (entry.isIntersecting) element.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      else { element.pause(); setPlaying(false); }
    }, { threshold: 0.25 });
    if (frameRef.current) observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    const element = videoRef.current;
    if (!element) return;
    if (element.paused) element.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    else { element.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
  };
  const fullscreen = () => videoRef.current?.requestFullscreen?.();

  return <Reveal className="video-frame">
    <div className="video-media" ref={frameRef}>
      <video ref={videoRef} className={`video-player ${loaded ? "is-loaded" : ""}`} src={mediaSrc(video.url)} poster={photoSrc(video.poster_url)} crossOrigin="anonymous" autoPlay={!reduced} muted={muted} loop playsInline preload="metadata" onLoadedData={() => setLoaded(true)} data-testid={`video-gallery-player-${index}`} aria-label={video.title} />
      {!loaded && <span className="video-loading" data-testid={`video-gallery-loading-${index}`}>Syncing film…</span>}
      <span className="video-badge" data-testid={`video-gallery-badge-${index}`}>{video.badge}</span>
      <div className="video-hud"><button type="button" onClick={togglePlay} aria-label={playing ? `Pause ${video.title}` : `Play ${video.title}`} data-testid={`video-gallery-play-btn-${index}`}>{playing ? "Pause" : "Play"}</button><button type="button" onClick={toggleMute} aria-label={muted ? `Unmute ${video.title}` : `Mute ${video.title}`} data-testid={`video-gallery-mute-btn-${index}`}>{muted ? "Sound off" : "Sound on"}</button><button type="button" onClick={fullscreen} aria-label={`View ${video.title} fullscreen`} data-testid={`video-gallery-fullscreen-btn-${index}`}>Full screen</button></div>
    </div>
    <div className="video-copy"><h3 data-testid={`video-gallery-title-${index}`}>{video.title}</h3><p data-testid={`video-gallery-caption-${index}`}>{video.caption}</p><span className="video-sr-only">Video: {video.title}. {video.caption}</span></div>
  </Reveal>;
}

export default function VideoGallery({ videos = [] }) {
  const activeVideos = videos.filter((video) => video.active !== false && video.url);
  if (!activeVideos.length) return null;
  return <section className="video-gallery" id="live-hearth" data-testid="video-gallery-section-container"><div className="container"><Reveal className="video-gallery-head"><span className="eyebrow">Kitchen broadcast · live hearth</span><h2 className="section-title">Craft in motion.</h2><p>Watch the hands, the heat, and the final walk to the table. Two short films from the Fucino Pizza kitchen, made to be watched with the sound on when you are ready.</p></Reveal><div className="video-grid">{activeVideos.map((video, index) => <VideoFrame key={`${video.slot}-${video.url}`} video={video} index={index} />)}</div><Reveal className="video-stats"><div><strong>850°F</strong><span>Hearth heat</span></div><div><strong>90 sec</strong><span>Flash bake</span></div><div><strong>72 hr</strong><span>Sourdough patience</span></div><div><strong>100%</strong><span>Made for the table</span></div></Reveal></div></section>;
}
